'use client'
import React, { useState, useEffect } from 'react';
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSave,
    FaSync,
} from 'react-icons/fa';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify"
import { API_BASE_URL } from '../../utils/CONSTANTS'
import Header from '../../components/header';

const getTodayISO = () => {
    const d = new Date();
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d - tzOffset).toISOString().slice(0, 10);
};

const LS_KEY = (date) => `daily_reports_${date}`;

const readLocalForDate = (date) => {
    try {
        const raw = localStorage.getItem(LS_KEY(date));
        if (!raw) return [];
        return JSON.parse(raw);
    } catch (e) {
        console.error("local read error", e);
        return [];
    }
};

const writeLocalForDate = (date, arr) => {
    try {
        localStorage.setItem(LS_KEY(date), JSON.stringify(arr || []));
    } catch (e) {
        console.error("local write error", e);
    }
};

const saveLocalEntry = (entry) => {
    const date = entry.date || getTodayISO();
    const arr = readLocalForDate(date);
    const item = { ...entry };
    if (!item._id && !item._localId) item._localId = Date.now().toString();
    arr.push(item);
    writeLocalForDate(date, arr);
    return item;
};

const updateLocalEntry = (entry) => {
    const date = entry.date || getTodayISO();
    const arr = readLocalForDate(date);
    const idx = arr.findIndex((r) => (r._localId && entry._localId && r._localId === entry._localId));
    if (idx >= 0) {
        arr[idx] = entry;
        writeLocalForDate(date, arr);
        return true;
    }
    const idx2 = arr.findIndex((r) => r._id && entry._id && r._id === entry._id);
    if (idx2 >= 0) {
        arr[idx2] = entry;
        writeLocalForDate(date, arr);
        return true;
    }
    return false;
};

export default function DailyProductionform() {
    const [alloperatorName, setAlloperatorName] = useState([]);
    const [cardNoList, setCardNoList] = useState([]);
    const [isMaximized, setIsMaximized] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const initialValue = {
        machineNo: "",
        partNo: "",
        operatorName: "",
        customOperatorName: null,
        cycleTime: 0,
        count: 0,
        target: 0,
        actualCount: 0,
        effeciencyPercent: 0,
        startTime: "",
        stopTime: "",
        description: "",
        date: getTodayISO(),
        _localId: null,
        _id: null,
    };

    const { mode, id } = location.state || {}
    const [formData, setFormData] = useState(initialValue)

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/product/getOperatorNames`)
            .then(response => {
                const allOperatorNames = (response.data.operators || [])
                    .map(item => item.customOperatorName)
                    .filter(name => name !== null && name !== undefined);

                const uniqueOperatorNames = [...new Set(allOperatorNames)];
                setAlloperatorName(uniqueOperatorNames);
            })
            .catch(error => console.error("Error fetching operator names:", error));
    }, []);

    useEffect(() => {
        const fetchById = async () => {
            if (!id) return;
            try {
                const response = await axios.get(`${API_BASE_URL}/api/product/getdailyreport/${id}`);
                if (response.status === 200) {
                    const item = Array.isArray(response.data.isdata) ? response.data.isdata[0] : response.data.isdata;
                    if (item) {
                        const dateStr = item.date ? new Date(item.date).toISOString().slice(0,10) : getTodayISO();
                        setFormData({ ...item, date: dateStr, _localId: null });
                    }
                }
            } catch (error) {
                console.error("Error fetching item:", error.message);
            }
        };
        fetchById();
    }, [id]);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/order/getCardno`)
            .then(response => {
                if (response.status === 200) {
                    const sortedCardNos = response.data.cardNos.sort((a, b) => Number(b.cardNo) - Number(a.cardNo));
                    setCardNoList(sortedCardNos);
                }
            })
            .catch(error => {
                console.error("Error fetching card numbers:", error.message);
            });
    }, []);

    useEffect(() => {
        if (formData.count) {
            let target = formData.cycleTime * 9.5 * 60 + Number(formData.count)
            setFormData(prev => ({ ...prev, target }))
        }
    }, [formData.count, formData.cycleTime])

    useEffect(() => {
        let efficiency = Math.round(((formData.actualCount - formData.count) / (formData.target - formData.count)) * 100)
        efficiency = isNaN(efficiency) ? 0 : efficiency;
        setFormData(prev => ({ ...prev, effeciencyPercent: efficiency.toFixed(2) }))
    }, [formData.actualCount, formData.target])

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAdd = async () => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/product/postdailyreport`, formData, {
                headers: { 'Content-Type': "application/json" }
            });

            if (response.status === 201) {
                toast("Report created");
                const saved = response.data?.data || null;
                if (saved && saved._id) {
                    const date = formData.date || getTodayISO();
                    const arr = readLocalForDate(date);
                    const filtered = arr.filter((r) => !(r._localId && formData._localId && r._localId === formData._localId));
                    filtered.push({ ...saved, date });
                    writeLocalForDate(date, filtered);
                } else {
                    saveLocalEntry(formData);
                }
                try { window.dispatchEvent(new Event("operatorListUpdated")); } catch (e) {}
                navigate("/dailyproductionmenu");
            } else {
                saveLocalEntry(formData);
                toast("Saved locally (unexpected server response).");
                navigate("/dailyproductionmenu");
            }
        } catch (err) {
            console.warn("Add failed, saving locally:", err?.message || err);
            saveLocalEntry(formData);
            toast("Saved locally (offline fallback).");
            navigate("/dailyproductionmenu");
        }
    }

    const handleUpdate = async () => {
        try {
            if (formData._id) {
                const response = await axios.post(`${API_BASE_URL}/api/product/updatedailyreport`, formData, {
                    headers: { 'Content-Type': "application/json" }
                });
                if (response.status === 201) {
                    toast("Report updated");
                    updateLocalEntry(formData);
                    try { window.dispatchEvent(new Event("operatorListUpdated")); } catch (e) {}
                    navigate("/dailyproductionmenu");
                    return;
                }
            }
            if (formData._localId) {
                updateLocalEntry(formData);
                toast("Local report updated");
                navigate("/dailyproductionmenu");
                return;
            }
            await handleAdd();
        } catch (err) {
            console.warn("Update failed, updating local copy", err);
            updateLocalEntry(formData);
            toast("Updated locally (fallback).");
            navigate("/dailyproductionmenu");
        }
    }

    return (
        <>
            <Header />

            <div className="p-2 flex items-center justify-center flex-wrap ">
                <div
                    className={`bg-blue-200 shadow-xl ${isMaximized ? 'w-full h-full' : 'w-[400px] lg:w-[600px]'
                        }`}>
                    <div className="flex items-center justify-between bg-gradient-to-r from-teal-600 to-teal-700 p-0.5">
                        <span className="text-white font-semibold px-2">Daily Production Reports</span>
                        <div className="flex gap-0.5">
                            <button
                                className="w-4 h-4 flex items-center justify-center hover:bg-slate-50 text-white hover:text-teal-700"
                                onClick={() => setIsMaximized(!isMaximized)}
                            >
                                <span>□</span>
                            </button>
                        </div>
                    </div>
                    <div className="p-2">
                        <div className="mb-2">
                            <div className="grid grid-cols-3 bg-[#E0FFFF] gap-2 mt-2 p-2">
                                <div className="">
                                    <label className="block mb-0.5">Machine No.</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-1 py-0.5"
                                        name="machineNo"
                                        value={formData.machineNo}
                                        onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                    />
                                </div>
                                <div className="">
                                    <label className="block mb-0.5">Card No.</label>
                                    <select
                                        className="w-full border border-gray-300 rounded px-1 py-0.5"
                                        name="partNo"
                                        value={formData.partNo}
                                        onChange={(e) => setFormData({ ...formData, partNo: e.target.value })}
                                    >
                                        <option value="">Select a Card No.</option>
                                        {cardNoList.map((card, index) => (
                                            <option key={index} value={card.cardNo}>
                                                {card.cardNo}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Date picker */}
                                <div>
                                    <label className="block mb-0.5">Date</label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded px-1 py-0.5"
                                        name="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block mb-0.5">Operator Name</label>
                                    <select
                                        className="w-full border border-gray-300 rounded px-1 py-1"
                                        name="operatorName"
                                        value={formData.operatorName}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Operator</option>
                                        {[...new Set(alloperatorName)].map((value, index) => (
                                            <option key={index} value={value} className="text-black">
                                                {value}
                                            </option>
                                        ))}
                                        <option value="Other" className="text-black">
                                            Other
                                        </option>
                                    </select>

                                    {formData.operatorName === "Other" && (
                                        <input
                                            type="text"
                                            name="customOperatorName"
                                            placeholder="Enter custom Operator name"
                                            value={formData.customOperatorName || ""}
                                            onChange={(e) =>
                                                handleInputChange({
                                                    target: {
                                                        name: "customOperatorName",
                                                        value: e.target.value,
                                                    },
                                                })
                                            }
                                            className="w-full border border-gray-300 rounded p-0.5 text-sm mt-2"
                                        />
                                    )}

                                </div>
                                <div>
                                    <label className="block mb-0.5">Cycle Time (pcs/min)</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="number"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="cycleTime"
                                            value={formData.cycleTime}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-0.5">Count(8:30 AM)</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="number"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="count"
                                            value={formData.count}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-0.5">Target(6:00 PM)</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="number"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="target"
                                            value={formData.target}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-0.5">Actual Count(6:00 PM)</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="number"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="actualCount"
                                            value={formData.actualCount}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-0.5">%</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="number"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="effeciencyPercent"
                                            value={formData.effeciencyPercent}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-0.5">Start Time</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="startTime"
                                            value={formData.startTime}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-0.5">Stop Time</label>
                                    <div className="flex gap-1">
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded px-1 py-0.5"
                                            name="stopTime"
                                            value={formData.stopTime}
                                            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="col-span-3">
                                    <label className="block mb-0.5">Reason</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-1 py-0.5"
                                        name="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between mt-2 bg-teal-600 p-1 flex-wrap ">
                            <div className='flex mb-3  lg:m-0  '>
                                <div className="flex gap-1">
                                    {mode == "add" || !mode ?
                                        <button
                                            className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded"
                                            onClick={handleAdd}
                                        >
                                            <FaSave className="h-3 w-3" />
                                            {'Save'}
                                        </button>
                                        :
                                        null
                                    }
                                    {mode == "update" ?
                                        <button
                                            className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded"
                                            onClick={handleUpdate}
                                        >
                                            <FaEdit className="h-3 w-3" />
                                            Update
                                        </button>
                                        :
                                        null
                                    }
                                </div>
                            </div>
                            <div className="flex gap-1">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
